import * as React from "react";
import {
	Alert,
	AlertTitle,
	Autocomplete,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";

import type { CampaignRequestDocument, DocumentKind } from "@/hooks/api/use-campaign-requests";
import {
	campaignRequestErrorMessage,
	useQuoteCampaignRequest,
	useUpdateCampaignRequest,
} from "@/hooks/api/use-campaign-requests";
import { useAuth } from "@/components/core/auth-provider";

import { QuoteLinesTable } from "../components";
import { DocumentRows, DocumentUploadButton } from "../components/document-list";
import { formatMoney, formatMoneyRange, PRICING_PENDING_CLIENT_COPY, quoteAssumptions } from "../constants";
import { sameStrings, sameText } from "./dirty";
import type { StepProps } from "./wizard-shell";
import { StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 7 — commercials.
 *
 * The quote comes from the API and only from the API. Nothing on this screen adds a line up: every
 * figure is a rate the price book held at the moment we priced it, frozen onto the line, so the
 * total a client reads here is the total we will bill against.
 *
 * Fixed is firm; call time is a range with its assumptions published beside it. Publishing the
 * reach rate and the average duration is what prevents the "why is this more than the quote"
 * conversation — the client can see exactly which assumption moved.
 *
 * Pricing can refuse. A rate that is still a placeholder, a request with no agents on it, or a
 * quote that has gone stale all come back as a 422 with a message naming the cause, and that
 * message is shown plainly rather than translated into something reassuring.
 */

interface PaperworkRowProps {
	requestId: string;
	/** Names the document. The upload beside it needs no label of its own. */
	title: string;
	kind: Extract<DocumentKind, "po" | "sow">;
	documents: CampaignRequestDocument[];
	placeholder: string;
	value: string;
	onChange: (value: string) => void;
	required: boolean;
	readOnly?: boolean;
	help?: string;
}

/**
 * One document, one row: what it is called, and the file itself.
 *
 * These were two separate areas — a commercials card holding two reference fields, and an
 * "attached paperwork" list under it holding the files those references named. A client filling in
 * a PO number had to scroll past a stakeholder field to attach the PO. The reference and the file
 * are the same errand, so they are one row.
 */
function PaperworkRow({
	requestId,
	title,
	kind,
	documents,
	placeholder,
	value,
	onChange,
	required,
	readOnly = false,
	help,
}: PaperworkRowProps): React.JSX.Element {
	return (
		<Stack spacing={1.5}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
				<Typography variant="subtitle2">{title}</Typography>
				{required ? <Chip color="warning" label="Required" size="small" variant="soft" /> : null}
			</Stack>

			<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
				<TextField
					fullWidth
					size="small"
					disabled={readOnly}
					label="Reference"
					placeholder={placeholder}
					value={value}
					onChange={(event) => onChange(event.target.value)}
				/>

				{readOnly ? null : <DocumentUploadButton requestId={requestId} kind={kind} label="Upload" size="medium" />}
			</Stack>

			{documents.length > 0 ? (
				<DocumentRows requestId={requestId} documents={documents} readOnly={readOnly} showKind={false} />
			) : null}

			{help ? (
				<Typography variant="caption" color="text.secondary">
					{help}
				</Typography>
			) : null}
		</Stack>
	);
}

export function StepQuote({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const { user } = useAuth();
	const isSuperAdmin = Boolean(user?.isSuperAdmin);

	const quoteMutation = useQuoteCampaignRequest();
	const updateMutation = useUpdateCampaignRequest();

	const [quoteError, setQuoteError] = React.useState<string | null>(null);
	const [sowReference, setSowReference] = React.useState(request.sowReference ?? "");
	const [poReference, setPoReference] = React.useState(request.poReference ?? "");
	const [stakeholders, setStakeholders] = React.useState<string[]>(request.stakeholderEmails ?? []);

	const quote = request.currentQuote;
	const sowDocuments = request.documents.filter((document) => document.kind === "sow");
	const poDocuments = request.documents.filter((document) => document.kind === "po");

	/**
	 * Re-price. Ours alone, and the button behind it only appears for a super-admin.
	 *
	 * `POST /quote` carries no client role on purpose — it sets what the client is asked to pay —
	 * so a wizard that called it for everybody would 403 for every owner, manager and admin on the
	 * account and leave the request with nothing to confirm. The client's quote is produced by the
	 * server when they submit (canvas §01: "submit → quote ready", nobody standing on the arrow),
	 * and this screen simply reads `currentQuote` off the request.
	 */
	const price = React.useCallback(async (): Promise<void> => {
		setQuoteError(null);

		try {
			await quoteMutation.mutateAsync({ id: request.id });
		} catch (error: any) {
			// Verbatim. "No rate configured for: translated_agent" is the whole answer, and softening
			// it into "something went wrong" costs an operator the one clue they need.
			setQuoteError(campaignRequestErrorMessage(error, "This request could not be priced."));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- the mutation object is new each render.
	}, [request.id]);

	const assumptions = quoteAssumptions(quote?.assumptions);
	const currency = quote?.currency ?? "SGD";

	/**
	 * A request in `changes_requested` has been priced and had that price withdrawn by an edit —
	 * `supersedeOnEdit()` supersedes the version and bounces the state. It is the one case where
	 * "priced when you send this to us" is flatly wrong: it was sent, and it was priced.
	 */
	const withdrawn = !quote && request.state === "changes_requested";

	// What is true of the price right now, in one line, because the panel that would have
	// explained it belongs on step 8 where the quote is confirmed.
	const heading = quote
		? "Assembled from what you chose."
		: withdrawn
			? "Your changes withdrew the last quote. This step takes the paperwork."
			: request.state === "draft"
				? "Priced when you send this to us. This step takes the paperwork."
				: "Being priced by us. This step takes the paperwork.";

	/**
	 * Write only what moved.
	 *
	 * `PUT /campaign-requests/:id` runs `recompute()`, and `recompute()` supersedes any live quote
	 * and walks a `quoted` request back to `changes_requested`. Writing unconditionally on Continue
	 * therefore withdrew the client's own quote every time they re-opened this step and pressed the
	 * button — leaving "The request was edited after it was priced" on the timeline they read
	 * (UX §12) and require a replacement quote before confirmation.
	 */
	const dirty =
		!sameText(sowReference, request.sowReference) ||
		!sameText(poReference, request.poReference) ||
		!sameStrings(stakeholders, request.stakeholderEmails ?? []);

	// The same answer the save uses, so the rail cannot decide there is nothing to lose while the
	// save is about to write, or the other way round.
	useWizardDirty(dirty);

	const save = async (): Promise<boolean> => {
		if (!dirty) return true;

		try {
			await updateMutation.mutateAsync({
				id: request.id,
				data: {
					sowReference: sowReference.trim() || null,
					poReference: poReference.trim() || null,
					stakeholderEmails: stakeholders,
				},
			});
			return true;
		} catch {
			return false;
		}
	};

	const handleContinue = async (): Promise<void> => {
		if (await save()) goTo(8);
	};

	const handleSaveExit = async (): Promise<void> => {
		if (await save()) exit();
	};

	return (
		<Stack spacing={3}>
			{/* One line, and it changes with the state rather than standing beside a panel
			    explaining what has not happened yet. */}
			<StepHeading title="Your quote" help={heading} />

			{quoteError ? (
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" onClick={() => void price()}>
							Try Again
						</Button>
					}
				>
					<AlertTitle>We could not price this request</AlertTitle>
					{quoteError}
				</Alert>
			) : null}

			{/* A request that has been submitted and still has no quote is a gap in our own rates,
			    not something the client did. Say that, rather than showing them a dead screen —
			    and say it in the same words the review screen and the request page use. */}
			{!quote && !isSuperAdmin && request.state !== "draft" ? (
				<Alert severity="info">
					{/* Named by its cause. "We're finalising your pricing" over a quote that was
					    withdrawn by the client's own edit reads as us being slow, and leaves them
					    hunting step 8 for the version they already saw. */}
					<AlertTitle>
						{withdrawn ? "Your changes withdrew the earlier quote" : "We’re finalising your pricing"}
					</AlertTitle>
					{PRICING_PENDING_CLIENT_COPY}
				</Alert>
			) : null}

			{/* Admins can inspect unresolved pricing on the review step. */}
			{withdrawn && isSuperAdmin ? (
				<Alert severity="warning">
					<AlertTitle>This request has no live quote</AlertTitle>
					An edit superseded the last version. Saved changes trigger server pricing; use the review step to retry and
					inspect any unresolved rates.
				</Alert>
			) : null}

			{quoteMutation.isPending ? (
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					<CircularProgress size={18} />
					<Typography variant="body2" color="text.secondary">
						Pricing against your rates…
					</Typography>
				</Stack>
			) : null}

			{quote ? (
				<Stack spacing={2}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={1}
						sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
					>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							<Typography variant="h6">{request.title}</Typography>
							<Chip label={currency} size="small" variant="outlined" />
							<Chip label={`Version ${quote.version}`} size="small" variant="outlined" />
						</Stack>

						{/* Ours. `POST /quote` grants no client role — it sets what the client is asked
						    to pay — so offering the button to a client would only ever produce a 403. */}
						{isSuperAdmin ? (
							<Button
								size="small"
								startIcon={<ArrowsClockwiseIcon />}
								disabled={quoteMutation.isPending}
								onClick={() => void price()}
							>
								Re-price
							</Button>
						) : null}
					</Stack>

					<QuoteLinesTable
						lines={quote.lines}
						currency={currency}
						fixedTotal={quote.fixedTotal}
						variableLow={quote.variableLow}
						variableHigh={quote.variableHigh}
					/>

					<Card variant="outlined" sx={{ p: 2 }}>
						<Stack spacing={1}>
							<Typography variant="subtitle2">What the Estimate Assumes</Typography>

							{assumptions.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									This quote carries no variable lines, so there is nothing to estimate.
								</Typography>
							) : (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{assumptions.map((assumption) => (
										<Chip
											key={assumption.key}
											label={`${assumption.label}: ${assumption.value}`}
											size="small"
											variant="outlined"
										/>
									))}
								</Box>
							)}

							<Typography variant="body2" color="text.secondary">
								Fixed {formatMoney(quote.fixedTotal, currency)} · estimated{" "}
								{formatMoneyRange(quote.variableLow, quote.variableHigh, currency)}.
							</Typography>
						</Stack>
					</Card>
				</Stack>
			) : null}

			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={3}>
					<Typography variant="subtitle1">Paperwork</Typography>

					<PaperworkRow
						requestId={request.id}
						title="Statement of work"
						kind="sow"
						documents={sowDocuments}
						placeholder="MSA 2026"
						value={sowReference}
						onChange={setSowReference}
						required={request.sowRequired}
						readOnly={readOnly}
					/>

					<PaperworkRow
						requestId={request.id}
						title="Purchase order"
						kind="po"
						documents={poDocuments}
						placeholder="PO-4471"
						value={poReference}
						onChange={setPoReference}
						required={request.poRequired}
						readOnly={readOnly}
						help="Appears on the invoice."
					/>

					<Autocomplete
						multiple
						freeSolo
						disabled={readOnly}
						options={[] as string[]}
						value={stakeholders}
						onChange={(_event, value) => setStakeholders(value as string[])}
						renderValue={(selected, getItemProps) =>
							selected.map((email, index) => {
								const { key, ...itemProps } = getItemProps({ index });
								return <Chip {...itemProps} key={key} label={email} size="small" />;
							})
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Stakeholder emails"
								// Not "send the invoice to": some clients receive a PO through a procurement
								// system and want the invoice submitted back into it. The label has to survive both.
								helperText="Who receives the invoice and the statement."
							/>
						)}
					/>
				</Stack>
			</Card>

			<WizardFooter
				busy={updateMutation.isPending}
				onBack={() => goTo(6)}
				onSaveExit={readOnly ? undefined : () => void handleSaveExit()}
				onContinue={readOnly ? () => goTo(8) : () => void handleContinue()}
			/>
		</Stack>
	);
}
